import { HANDLE_DIRECTION, SHAPE_MIN_SIZE_FRACTION } from '@v2/components/imageHandling/components/DrawingCanvas/constants';
import {
  Annotation,
  AnnotationType,
  ArrowAnnotation,
  CircleAnnotation,
  HandleId,
  PenAnnotation,
  RectangleAnchorParams,
  RectangleAnnotation,
  ResizeAnnotationParams,
} from '../types';
import type { Position } from '@interfaces/base';

const isCornerHandle = (handleId: HandleId): boolean =>
  handleId === HandleId.TopLeft ||
  handleId === HandleId.TopRight ||
  handleId === HandleId.BottomLeft ||
  handleId === HandleId.BottomRight;

const getRectangleAnchor = ({ handleId, origin, width, height }: RectangleAnchorParams): Position => {
  const right = origin.x + width;
  const bottom = origin.y + height;
  switch (handleId) {
    case HandleId.TopLeft:
      return { x: right, y: bottom };
    case HandleId.TopRight:
      return { x: origin.x, y: bottom };
    case HandleId.BottomLeft:
      return { x: right, y: origin.y };
    case HandleId.BottomRight:
      return { x: origin.x, y: origin.y };
    case HandleId.TopMid:
      return { x: origin.x + width / 2, y: bottom };
    case HandleId.BottomMid:
      return { x: origin.x + width / 2, y: origin.y };
    case HandleId.MidLeft:
      return { x: right, y: origin.y + height / 2 };
    case HandleId.MidRight:
      return { x: origin.x, y: origin.y + height / 2 };
    default:
      return { x: origin.x, y: origin.y };
  }
};

const resizeRectangle = (params: ResizeAnnotationParams): RectangleAnnotation => {
  const { annotation, handleId, pointerPosition: pos, dragStartSnapshot, shiftKey } = params;
  const start = dragStartSnapshot as RectangleAnnotation;
  const { origin, width: w, height: h } = start;
  const right = origin.x + w;
  const bottom = origin.y + h;

  let nx = origin.x;
  let ny = origin.y;
  let nw = w;
  let nh = h;

  switch (handleId) {
    case HandleId.TopLeft:
      nx = pos.x;
      ny = pos.y;
      nw = right - pos.x;
      nh = bottom - pos.y;
      break;
    case HandleId.TopRight:
      ny = pos.y;
      nw = pos.x - origin.x;
      nh = bottom - pos.y;
      break;
    case HandleId.BottomLeft:
      nx = pos.x;
      nw = right - pos.x;
      nh = pos.y - origin.y;
      break;
    case HandleId.BottomRight:
      nw = pos.x - origin.x;
      nh = pos.y - origin.y;
      break;
    case HandleId.TopMid:
      ny = pos.y;
      nh = bottom - pos.y;
      break;
    case HandleId.BottomMid:
      nh = pos.y - origin.y;
      break;
    case HandleId.MidLeft:
      nx = pos.x;
      nw = right - pos.x;
      break;
    case HandleId.MidRight:
      nw = pos.x - origin.x;
      break;
    default:
      break;
  }

  if (nw < 0) {
    nx += nw;
    nw = -nw;
  }
  if (nh < 0) {
    ny += nh;
    nh = -nh;
  }

  if (shiftKey) {
    const size = isCornerHandle(handleId)
      ? Math.max(nw, nh)
      : handleId === HandleId.TopMid || handleId === HandleId.BottomMid
      ? nh
      : nw;

    const anchor = getRectangleAnchor({ handleId, origin, width: w, height: h });

    switch (handleId) {
      case HandleId.TopLeft:
        nx = anchor.x - size;
        ny = anchor.y - size;
        break;
      case HandleId.TopRight:
        nx = anchor.x;
        ny = anchor.y - size;
        break;
      case HandleId.BottomLeft:
        nx = anchor.x - size;
        ny = anchor.y;
        break;
      case HandleId.BottomRight:
        nx = anchor.x;
        ny = anchor.y;
        break;
      case HandleId.TopMid:
        nx = anchor.x - size / 2;
        ny = anchor.y - size;
        break;
      case HandleId.BottomMid:
        nx = anchor.x - size / 2;
        ny = anchor.y;
        break;
      case HandleId.MidLeft:
        nx = anchor.x - size;
        ny = anchor.y - size / 2;
        break;
      case HandleId.MidRight:
        nx = anchor.x;
        ny = anchor.y - size / 2;
        break;
      default:
        break;
    }
    nw = size;
    nh = size;
  }

  return {
    ...(annotation as RectangleAnnotation),
    origin: { x: nx, y: ny },
    width: Math.max(nw, SHAPE_MIN_SIZE_FRACTION),
    height: Math.max(nh, SHAPE_MIN_SIZE_FRACTION),
  };
};

const resizeCircle = (params: ResizeAnnotationParams): CircleAnnotation => {
  const { annotation, handleId, pointerPosition: pos, dragStartSnapshot, shiftKey } = params;
  const start = dragStartSnapshot as CircleAnnotation;
  let rx = start.radiusX;
  let ry = start.radiusY;

  if (handleId === HandleId.Top || handleId === HandleId.Bottom) {
    ry = Math.abs(pos.y - start.center.y);
  } else if (handleId === HandleId.Left || handleId === HandleId.Right) {
    rx = Math.abs(pos.x - start.center.x);
  } else if (isCornerHandle(handleId)) {
    rx = Math.abs(pos.x - start.center.x);
    ry = Math.abs(pos.y - start.center.y);
  }

  if (shiftKey) {
    const r = isCornerHandle(handleId)
      ? Math.max(rx, ry)
      : handleId === HandleId.Top || handleId === HandleId.Bottom
      ? ry
      : rx;
    rx = r;
    ry = r;
  }

  return {
    ...(annotation as CircleAnnotation),
    radiusX: Math.max(rx, SHAPE_MIN_SIZE_FRACTION),
    radiusY: Math.max(ry, SHAPE_MIN_SIZE_FRACTION),
  };
};

const resizeArrow = (params: ResizeAnnotationParams): ArrowAnnotation => {
  const { annotation, handleId, pointerPosition: pos } = params;
  const base = annotation as ArrowAnnotation;
  if (handleId === HandleId.Start) return { ...base, start: pos };
  if (handleId === HandleId.End) return { ...base, end: pos };
  return base;
};

const resizePenOrHighlighter = (params: ResizeAnnotationParams): PenAnnotation => {
  const { annotation, handleId, pointerPosition: pos, dragStartSnapshot } = params;
  const start = dragStartSnapshot as PenAnnotation;
  if (start.points.length < 2) return annotation as PenAnnotation;

  const xs = start.points.map((p) => p.x);
  const ys = start.points.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const oldW = maxX - minX || 1;
  const oldH = maxY - minY || 1;

  let nMinX = minX;
  let nMinY = minY;
  let nMaxX = maxX;
  let nMaxY = maxY;
  const { horiz, vert } = HANDLE_DIRECTION[handleId];
  if (horiz === 'left') nMinX = pos.x;
  if (horiz === 'right') nMaxX = pos.x;
  if (vert === 'top') nMinY = pos.y;
  if (vert === 'bottom') nMaxY = pos.y;
  const nW = nMaxX - nMinX || 1;
  const nH = nMaxY - nMinY || 1;

  return {
    ...(annotation as PenAnnotation),
    points: start.points.map((p) => ({
      x: nMinX + ((p.x - minX) / oldW) * nW,
      y: nMinY + ((p.y - minY) / oldH) * nH,
    })),
  };
};

/**
 * Handle-based resize. Returns the annotation with geometry re-computed
 * based on the current pointer position and modifier keys (Shift for perfect shape).
 */
export const resizeAnnotation = (params: ResizeAnnotationParams): Annotation => {
  switch (params.annotation.type) {
    case AnnotationType.Rectangle:
      return resizeRectangle(params);
    case AnnotationType.Circle:
      return resizeCircle(params);
    case AnnotationType.Arrow:
      return resizeArrow(params);
    case AnnotationType.Pen:
    case AnnotationType.Highlighter:
      return resizePenOrHighlighter(params);
    default:
      return params.annotation;
  }
};
